import { Fragment } from 'react';
import { splitMentionText, type MentionableUser } from '@overlayqa/shared';

interface FormattedDescriptionProps {
  text: string;
  className?: string;
  /** Teammates: their `@Name` mentions render bold brand blue (the server's mention rule, shared). */
  members?: MentionableUser[];
}

/**
 * Renders issue descriptions with basic formatting.
 * Handles the markdown subset used in a11y and AI-generated descriptions:
 * - **bold** text
 * - Bullet lists (- item)
 * - Line breaks / paragraphs
 */
export function FormattedDescription({ text, className, members = [] }: FormattedDescriptionProps) {
  const paragraphs = text.split(/\n{2,}/);

  return (
    /* prose-measure sits OUTSIDE the ?? so a caller passing className cannot
       drop the reading measure. The default styling stays overridable; the
       line length is not, because the failure it prevents is invisible --
       nothing errors, the text just gets unreadable on a wide screen. */
    <div className={`prose-measure ${className ?? 'text-gray-600 text-sm space-y-3'}`}>
      {paragraphs.map((para, i) => {
        const trimmed = para.trim();
        if (!trimmed) return null;

        // Check if this paragraph is a bullet list
        const lines = trimmed.split('\n');
        const isList = lines.every((l) => l.trim().startsWith('- '));

        if (isList) {
          return (
            <ul key={i} className="list-disc list-inside space-y-1">
              {lines.map((line, j) => (
                <li key={j}>{renderInline(line.trim().slice(2), members)}</li>
              ))}
            </ul>
          );
        }

        // Mixed content: some lines may be bullets, others not
        const hasBullets = lines.some((l) => l.trim().startsWith('- '));
        if (hasBullets) {
          return (
            <div key={i} className="space-y-1">
              {lines.map((line, j) => {
                const t = line.trim();
                if (t.startsWith('- ')) {
                  return (
                    <div key={j} className="flex gap-2">
                      <span className="text-gray-400 select-none" aria-hidden="true">-</span>
                      <span>{renderInline(t.slice(2), members)}</span>
                    </div>
                  );
                }
                return <p key={j}>{renderInline(t, members)}</p>;
              })}
            </div>
          );
        }

        // Regular paragraph with line breaks
        return (
          <p key={i}>
            {lines.map((line, j) => (
              <Fragment key={j}>
                {j > 0 && <br />}
                {renderInline(line, members)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

/** Renders inline **bold** markers as <strong>, and teammate `@Name` mentions bold brand blue. */
function renderInline(text: string, members: MentionableUser[]): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
    }
    return <Fragment key={i}>{splitMentionText(part, members).map((r, j) => (r.mention ? <span key={j} className="font-semibold text-primary-500">{r.text}</span> : <Fragment key={j}>{r.text}</Fragment>))}</Fragment>;
  });
}
